import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config/api';
import SellSellSellIntro from './SellSellSellIntro';
import MultiRowConveyorBelt from './MultiRowConveyorBelt';

const SellSellSell = () => {
  const [showIntro, setShowIntro] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const [conveyorItems, setConveyorItems] = useState([]);
  const containerRef = useRef(null);

  // Fetch conveyor items from API
  useEffect(() => {
    const fetchConveyorItems = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/conveyor-items`);
        if (response.ok) {
          const items = await response.json();
          setConveyorItems(items);
        }
      } catch (error) {
        console.error('Error fetching conveyor items:', error);
        setConveyorItems([
          {
            title: 'Nike Running Shoes',
            description: 'The global running boom made athletic shoes a multi-billion dollar industry.',
            imagePath: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80',
            imageWidth: 150,
            speed: 20,
            rows: 2,
            height: 200,
            info: '1,200 running shoes sold/min'
          },
          {
            title: 'Soccer Balls',
            description: 'Soccer balls are the heart of the world\'s most popular sport with 4 billion fans.',
            imagePath: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=120&q=80',
            imageWidth: 120,
            speed: 15,
            rows: 1,
            height: 120,
            info: '800 soccer balls sold/min'
          }
        ]);
      }
    };

    fetchConveyorItems();
  }, []);

  // OPTIMIZED: Throttled scroll-linked gradient with passive listener
  useEffect(() => {
    if (showIntro) return;

    let ticking = false;

    const updateBg = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(document.documentElement.scrollHeight - windowHeight, 1);
      const progress = Math.min(scrollTop / documentHeight, 1);

      // Smoother color transitions
      const h1 = Math.round(320 + progress * 50);
      const s1 = Math.round(65 - progress * 25);
      const l1 = Math.round(75 + progress * 20);
      const h2 = Math.round(45 + progress * 30);
      const s2 = Math.round(75 - progress * 35);
      const l2 = Math.round(88 + progress * 8);

      const el = containerRef.current;
      if (el) {
        el.style.setProperty('--h1', h1);
        el.style.setProperty('--s1', `${s1}%`);
        el.style.setProperty('--l1', `${l1}%`);
        el.style.setProperty('--h2', h2);
        el.style.setProperty('--s2', `${s2}%`);
        el.style.setProperty('--l2', `${l2}%`);
      }

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateBg);
        ticking = true;
      }
    };

    // Initial update
    updateBg();
    
    // Use passive listener for better scroll performance
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [showIntro]);

  useEffect(() => {
    if (!showIntro) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const exit = () => {
      if (leaving) return;
      setLeaving(true);
      setTimeout(() => {
        setShowIntro(false);
        document.body.style.overflow = prev;
      }, 600);
    };

    const onWheel = () => exit();
    const onKey = (e) => {
      if (['ArrowDown', 'PageDown', 'Space', 'Enter'].includes(e.code || e.key)) exit();
    };
    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchMove = (e) => { if (Math.abs(e.touches[0].clientY - touchStartY) > 10) exit(); };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      document.body.style.overflow = prev;
    };
  }, [showIntro, leaving]);

  if (showIntro) {
    return <SellSellSellIntro leaving={leaving} />;
  }

  return (
    <div
      ref={containerRef}
      className="sell-container"
      style={{
        background: `linear-gradient(135deg,
          hsl(var(--h1, 320), var(--s1, 65%), var(--l1, 75%)),
          hsl(var(--h2, 45), var(--s2, 75%), var(--l2, 88%))
        )`
      }}
    >
      {/* Enhanced background with emojis */}
      <div className="bg-sports" aria-hidden="true">
        {[
          { s: '⚽', x: '4%', y: '10%', size: '140px', blur: 5, rot: -8, o: 0.28 },
          { s: '🏀', x: '16%', y: '18%', size: '150px', blur: 6, rot: 6, o: 0.26 },
          { s: '🏏', x: '30%', y: '10%', size: '140px', blur: 6, rot: -12, o: 0.24 },
          { s: '🏒', x: '44%', y: '18%', size: '160px', blur: 7, rot: 8, o: 0.24 },
          { s: '⚽', x: '60%', y: '12%', size: '145px', blur: 6, rot: -5, o: 0.26 },
          { s: '🏀', x: '76%', y: '20%', size: '165px', blur: 6, rot: 8, o: 0.26 },
          { s: '🏒', x: '6%', y: '46%', size: '160px', blur: 7, rot: 4, o: 0.24 },
          { s: '⚽', x: '20%', y: '46%', size: '145px', blur: 5, rot: 0, o: 0.28 },
          { s: '🏀', x: '36%', y: '52%', size: '150px', blur: 6, rot: -6, o: 0.26 },
          { s: '🏏', x: '52%', y: '44%', size: '160px', blur: 7, rot: -14, o: 0.24 },
          { s: '⚽', x: '68%', y: '50%', size: '140px', blur: 6, rot: -3, o: 0.26 },
          { s: '🏏', x: '4%', y: '80%', size: '145px', blur: 6, rot: -10, o: 0.24 },
          { s: '🏀', x: '22%', y: '86%', size: '155px', blur: 6, rot: 12, o: 0.26 },
          { s: '⚽', x: '42%', y: '78%', size: '145px', blur: 5, rot: -6, o: 0.28 },
          { s: '🏒', x: '78%', y: '80%', size: '150px', blur: 6, rot: -12, o: 0.24 }
        ].map((it, i) => (
          <div
            key={i}
            className="sport bg-sport"
            style={{
              left: it.x, top: it.y,
              fontSize: it.size,
              filter: `blur(${it.blur}px) drop-shadow(0 12px 22px rgba(0,0,0,0.2))`,
              transform: `rotate(${it.rot}deg) translateZ(0)`,
              opacity: it.o
            }}
          >
            {it.s}
          </div>
        ))}
        <div className="glow glow-a" />
        <div className="glow glow-b" />
      </div>

      {/* ENHANCED: 30+ More Visible Background Sports Equipment Images */}
      <div className="sports-background-images" aria-hidden="true">
        {/* Row 1 - Top Section */}
        <div className="bg-image football"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1606925797300-0b35e9d1794e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '2%', top: '3%', width: '160px', height: '160px',
            transform: 'rotate(-12deg) translateZ(0)', opacity: '0.65'
          }} />

        <div className="bg-image cricket-bat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '10%', top: '8%', width: '130px', height: '190px',
            transform: 'rotate(-25deg) translateZ(0)', opacity: '0.60'
          }} />

        <div className="bg-image tennis-racket"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '20%', top: '2%', width: '150px', height: '150px',
            transform: 'rotate(35deg) translateZ(0)', opacity: '0.63'
          }} />

        <div className="bg-image basketball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1519861531473-9200262188bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '32%', top: '6%', width: '140px', height: '140px',
            transform: 'rotate(-8deg) translateZ(0)', opacity: '0.67'
          }} />

        <div className="bg-image cap"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1588850561407-ed78c282e89b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '43%', top: '4%', width: '130px', height: '130px',
            transform: 'rotate(18deg) translateZ(0)', opacity: '0.61'
          }} />

        <div className="bg-image shoes"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1542291026-7eec264c27ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '55%', top: '7%', width: '170px', height: '110px',
            transform: 'rotate(-15deg) translateZ(0)', opacity: '0.65'
          }} />

        <div className="bg-image cricket-ball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1531415074968-036ba1b575da?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '68%', top: '3%', width: '95px', height: '95px',
            transform: 'rotate(45deg) translateZ(0)', opacity: '0.70'
          }} />

        <div className="bg-image volleyball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '78%', top: '8%', width: '120px', height: '120px',
            transform: 'rotate(-22deg) translateZ(0)', opacity: '0.63'
          }} />

        <div className="bg-image badminton"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '88%', top: '5%', width: '130px', height: '150px',
            transform: 'rotate(28deg) translateZ(0)', opacity: '0.62'
          }} />

        {/* Row 2 - Upper Middle Section */}
        <div className="bg-image boxing-gloves"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517838277536-f5f99be501cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '1%', top: '22%', width: '140px', height: '140px',
            transform: 'rotate(12deg) translateZ(0)', opacity: '0.65'
          }} />

        <div className="bg-image american-football"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1560272564-c83b66b1ad12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '12%', top: '25%', width: '130px', height: '90px',
            transform: 'rotate(-18deg) translateZ(0)', opacity: '0.64'
          }} />

        <div className="bg-image rugby-ball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1612871689836-00d93f3d0855?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '24%', top: '23%', width: '125px', height: '85px',
            transform: 'rotate(25deg) translateZ(0)', opacity: '0.66'
          }} />

        <div className="bg-image golf-club"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '36%', top: '20%', width: '120px', height: '170px',
            transform: 'rotate(28deg) translateZ(0)', opacity: '0.61'
          }} />

        <div className="bg-image baseball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1566577739112-5180d4bf9390?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '48%', top: '24%', width: '110px', height: '110px',
            transform: 'rotate(-18deg) translateZ(0)', opacity: '0.68'
          }} />

        <div className="bg-image baseball-bat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '60%', top: '22%', width: '140px', height: '60px',
            transform: 'rotate(-35deg) translateZ(0)', opacity: '0.62'
          }} />

        <div className="bg-image soccer-cleats"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1511886929837-354d827aae26?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '72%', top: '25%', width: '150px', height: '100px',
            transform: 'rotate(-8deg) translateZ(0)', opacity: '0.64'
          }} />

        <div className="bg-image swimming-goggles"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1530143790-d4ff0c4e9029?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '85%', top: '23%', width: '120px', height: '80px',
            transform: 'rotate(15deg) translateZ(0)', opacity: '0.63'
          }} />

        {/* Row 3 - Middle Section */}
        <div className="bg-image dumbbells"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '3%', top: '42%', width: '160px', height: '95px',
            transform: 'rotate(5deg) translateZ(0)', opacity: '0.64'
          }} />

        <div className="bg-image yoga-mat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '15%', top: '40%', width: '140px', height: '140px',
            transform: 'rotate(-12deg) translateZ(0)', opacity: '0.60'
          }} />

        <div className="bg-image table-tennis"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1593786481097-cc6c0b67dd85?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '27%', top: '43%', width: '120px', height: '120px',
            transform: 'rotate(-25deg) translateZ(0)', opacity: '0.63'
          }} />

        <div className="bg-image skateboard"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1547447134-cd3f5c716030?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '40%', top: '41%', width: '150px', height: '50px',
            transform: 'rotate(8deg) translateZ(0)', opacity: '0.66'
          }} />

        <div className="bg-image cricket-stumps"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '53%', top: '39%', width: '100px', height: '150px',
            transform: 'rotate(-15deg) translateZ(0)', opacity: '0.59'
          }} />

        <div className="bg-image tennis-ball"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '65%', top: '43%', width: '100px', height: '100px',
            transform: 'rotate(20deg) translateZ(0)', opacity: '0.67'
          }} />

        <div className="bg-image hockey-stick"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1515703407324-5f753afd8be8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '77%', top: '40%', width: '140px', height: '170px',
            transform: 'rotate(32deg) translateZ(0)', opacity: '0.62'
          }} />

        <div className="bg-image whistle"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587068768584-249933d1b2f2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '90%', top: '42%', width: '90px', height: '80px',
            transform: 'rotate(-10deg) translateZ(0)', opacity: '0.65'
          }} />

        {/* Row 4 - Lower Middle Section */}
        <div className="bg-image football-jersey"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551698618-1dfe5d97d256?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '1%', top: '60%', width: '130px', height: '150px',
            transform: 'rotate(18deg) translateZ(0)', opacity: '0.58'
          }} />

        <div className="bg-image bicycle-helmet"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517256673644-36ad11246d21?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '13%', top: '63%', width: '120px', height: '110px',
            transform: 'rotate(-15deg) translateZ(0)', opacity: '0.64'
          }} />

        <div className="bg-image shin-guards"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1622228923011-c093595df4b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '26%', top: '61%', width: '100px', height: '130px',
            transform: 'rotate(10deg) translateZ(0)', opacity: '0.62'
          }} />

        <div className="bg-image football-goal"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '38%', top: '59%', width: '170px', height: '120px',
            transform: 'rotate(-5deg) translateZ(0)', opacity: '0.57'
          }} />

        <div className="bg-image stopwatch"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1461896836934-ffe607ba8211?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '52%', top: '62%', width: '90px', height: '90px',
            transform: 'rotate(-20deg) translateZ(0)', opacity: '0.66'
          }} />

        <div className="bg-image weights"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517836357463-d25dfeac3438?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '64%', top: '60%', width: '150px', height: '100px',
            transform: 'rotate(8deg) translateZ(0)', opacity: '0.63'
          }} />

        <div className="bg-image trophy"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '77%', top: '63%', width: '110px', height: '140px',
            transform: 'rotate(-12deg) translateZ(0)', opacity: '0.65'
          }} />

        <div className="bg-image medal"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1587893904933-5e89e6a0b396?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '89%', top: '61%', width: '100px', height: '130px',
            transform: 'rotate(15deg) translateZ(0)', opacity: '0.67'
          }} />

        {/* Row 5 - Bottom Section */}
        <div className="bg-image jump-rope"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '4%', top: '80%', width: '130px', height: '90px',
            transform: 'rotate(-18deg) translateZ(0)', opacity: '0.68'
          }} />

        <div className="bg-image resistance-bands"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1598289431512-b97b0917affc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '16%', top: '82%', width: '120px', height: '100px',
            transform: 'rotate(12deg) translateZ(0)', opacity: '0.64'
          }} />

        <div className="bg-image sports-bottle"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1602143407151-7111542de6e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '29%', top: '81%', width: '80px', height: '130px',
            transform: 'rotate(-8deg) translateZ(0)', opacity: '0.66'
          }} />

        <div className="bg-image gym-bag"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1553062407-98eeb64c6a62?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '41%', top: '79%', width: '150px', height: '110px',
            transform: 'rotate(5deg) translateZ(0)', opacity: '0.62'
          }} />

        <div className="bg-image fitness-tracker"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1575311373937-040b8e1fd5b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '54%', top: '82%', width: '110px', height: '90px',
            transform: 'rotate(-15deg) translateZ(0)', opacity: '0.69'
          }} />

        <div className="bg-image sports-towel"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '67%', top: '80%', width: '130px', height: '100px',
            transform: 'rotate(10deg) translateZ(0)', opacity: '0.63'
          }} />

        <div className="bg-image knee-pads"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1612528443702-f6741f70a049?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '80%', top: '81%', width: '110px', height: '120px',
            transform: 'rotate(-20deg) translateZ(0)', opacity: '0.65'
          }} />

        <div className="bg-image sports-gloves"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1584735174965-e6ab3c11c89e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80)',
            left: '91%', top: '83%', width: '100px', height: '100px',
            transform: 'rotate(18deg) translateZ(0)', opacity: '0.67'
          }} />
      </div>

      {/* Main Header Section */}
      <div className="header-section">
        <div className="sell-pattern-main">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className={`sell-row ${i % 2 === 0 ? 'sell-right' : 'sell-left'}`}></div>
          ))}
        </div>
        <div className="header-title">
          <div className="main-title">SELL! SELL! SELL!</div>
        </div>
        <div className="first-desc">
          The ultimate marketplace explosion! From basic balls to premium equipment, if you're not selling, you're <em>losing</em>.
          Here are the products dominating the global market.
        </div>
      </div>

      {/* Dynamic Content Sections */}
      <div className="content-sections">
        {conveyorItems.map((item, index) => (
          <section key={item._id || index} className="section">
            <div className="desc">
              {item.description}
            </div>
            <MultiRowConveyorBelt
              imagePath={item.imagePath}
              imageWidth={item.imageWidth}
              height={item.height}
              speed={item.speed}
              info={item.info}
              title={item.title}
              rows={item.rows || 1}
            />
          </section>
        ))}
      </div>

      {/* ULTRA-OPTIMIZED CSS for silky-smooth scrolling */}
      <style jsx>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        html {
          scroll-behavior: smooth;
          scrollbar-gutter: stable;
          overflow-x: hidden;
        }

        body {
          overflow-x: hidden;
        }

        .sell-container {
          width: 100%;
          min-height: 100vh;
          overflow-x: hidden;
          font-family: 'Arial', sans-serif;
          position: relative;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          contain: layout style paint;
        }

        .bg-sports {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          contain: strict;
        }

        .bg-sports .sport {
          mix-blend-mode: multiply;
        }

        .sport {
          position: absolute;
          line-height: 1;
          user-select: none;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
        }

        .bg-sport {
          transform: translateZ(0);
          contain: layout style paint;
        }

        .glow {
          position: absolute;
          width: 50vw; height: 50vw;
          max-width: 760px; max-height: 760px;
          border-radius: 50%;
          filter: blur(55px);
          opacity: 0.34;
          background: radial-gradient(closest-side, rgba(255,255,255,0.65), rgba(255,255,255,0));
          transform: translateZ(0);
        }
        .glow-a { left: -10vw; top: 12vh; }
        .glow-b { right: -12vw; bottom: -6vh; }

        .sports-background-images {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
          contain: strict;
        }

        .bg-image {
          position: absolute;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          border-radius: 14px;
          filter: blur(3px) brightness(1.1) saturate(1.15) contrast(1.05);
          box-shadow: 0 14px 45px rgba(0,0,0,0.18);
          animation: floatSports 24s ease-in-out infinite alternate;
          contain: layout style paint;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
        }

        .bg-image:nth-child(odd) {
          animation-delay: -2s;
          animation-direction: alternate-reverse;
        }

        .bg-image:nth-child(even) {
          animation-delay: -5s;
        }

        .bg-image:nth-child(3n) {
          animation-delay: -8s;
        }

        .bg-image:nth-child(4n) {
          animation-delay: -11s;
        }

        @keyframes floatSports {
          0% { transform: translate3d(0, 0, 0) scale(1); }
          50% { transform: translate3d(0, -15px, 0) scale(1.03); }
          100% { transform: translate3d(0, 10px, 0) scale(0.97); }
        }

        .site-header {
          position: fixed;
          top: 12px;
          left: 12px;
          z-index: 50;
        }
        .site-logo {
          color: #fff;
          text-decoration: none;
          filter: drop-shadow(0 0 4px rgba(0,0,0,0.3));
          transition: transform 0.2s ease;
        }
        .site-logo:hover {
          transform: translateX(-5px);
        }
        .logo-text {
          font-size: 16px;
          font-weight: 600;
        }

        .header-section {
          height: 100vh;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          z-index: 1;
          padding: 0 16px;
          contain: layout style;
        }

        .sell-pattern-main {
          position: absolute;
          inset: 0;
          overflow: hidden;
          contain: strict;
        }

        .sell-row {
          position: absolute;
          width: calc(100% + 100px);
          height: 26px;
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 26"><text x="10" y="20" font-family="Arial Black" font-size="14" fill="%23000" opacity="0.1">SELL! SELL! SELL! SELL! SELL! SELL! SELL! SELL!</text></svg>');
          background-size: 100px auto;
          opacity: 0.2;
          contain: layout style paint;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
        }

        .sell-right {
          animation: moveSellRight 4s linear infinite;
        }
        .sell-left {
          animation: moveSellLeft 4s linear infinite;
        }

        .sell-row:nth-child(1) { top: 0%; }
        .sell-row:nth-child(2) { top: 7%; }
        .sell-row:nth-child(3) { top: 14%; }
        .sell-row:nth-child(4) { top: 21%; }
        .sell-row:nth-child(5) { top: 28%; }
        .sell-row:nth-child(6) { top: 35%; }
        .sell-row:nth-child(7) { top: 42%; }
        .sell-row:nth-child(8) { top: 49%; }
        .sell-row:nth-child(9) { top: 56%; }
        .sell-row:nth-child(10) { top: 63%; }
        .sell-row:nth-child(11) { top: 70%; }
        .sell-row:nth-child(12) { top: 77%; }
        .sell-row:nth-child(13) { top: 84%; }
        .sell-row:nth-child(14) { top: 91%; }
        .sell-row:nth-child(15) { top: 98%; }

        @keyframes moveSellRight {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-100px, 0, 0); }
        }
        @keyframes moveSellLeft {
          0% { transform: translate3d(-100px, 0, 0); }
          100% { transform: translate3d(0, 0, 0); }
        }

        .header-title {
          position: relative;
          z-index: 10;
          text-align: center;
          margin-bottom: 40px;
        }
        .main-title {
          font-size: clamp(3rem, 8vw, 6rem);
          font-weight: 900;
          color: #1f1f1f;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .first-desc {
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 16px;
          color: #1f1f1f;
          font-size: clamp(0.95rem, 2.2vw, 1.25rem);
          line-height: 1.6;
          margin: 20px auto 0;
          max-width: min(680px, 88%);
          padding: clamp(16px, 2.5vw, 24px) clamp(18px, 3.2vw, 28px);
          text-align: center;
          position: relative;
          z-index: 10;
          box-shadow: 0 12px 32px rgba(0,0,0,0.12);
          contain: layout style paint;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .content-sections {
          padding: 60px 0;
          z-index: 1;
          position: relative;
          contain: layout style;
        }
        .section {
          margin-bottom: 80px;
          contain: layout style;
        }

        .desc {
          border-radius: 12px;
          color: #1f1f1f;
          font-size: 24px;
          line-height: 160%;
          margin: 40px auto 30px;
          max-width: min(600px, 92%);
          padding: 20px 24px;
          text-align: center;
          contain: layout style paint;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          font-weight: 500;
        }

        .belt-container {
          display: block;
          text-align: center;
          margin: 0 auto;
          width: 100vw;
          max-width: 100%;
          contain: layout style;
          position: relative;
          left: 50%;
          transform: translateX(-50%);
        }

        .multi-belt-shell {
          overflow: hidden;
          contain: layout style;
          line-height: 0;
          font-size: 0;
        }

        .multi-belt-stack {
          display: block;
          line-height: 0;
          font-size: 0;
          margin: 0;
          padding: 0;
        }

        .multi-belt-row {
          border-bottom: 1px solid rgba(255,255,255,0.05);
          contain: strict;
          position: relative;
          overflow: hidden;
        }
        .multi-belt-row:last-child {
          border-bottom: none;
        }

        .belt-track-wrapper {
          position: relative;
          width: 100%;
          overflow: hidden;
          contain: strict;
        }

        .belt-track {
          position: absolute;
          width: 300%;
          height: 100%;
          left: 0;
          top: 0;
          will-change: transform;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform-style: preserve-3d;
          contain: strict;
          animation-name: beltScrollRight;
          animation-duration: var(--speed);
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          animation-fill-mode: both;
          background-attachment: scroll;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }

        @keyframes beltScrollRight {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(calc(var(--tile-width) * -1), 0, 0); }
        }

        .multi-belt-row:hover .belt-track {
          animation-play-state: paused;
        }

        .belt-info-wrapper {
          display: block;
          text-align: center;
          margin-top: 16px;
        }
        .belt-info {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 12px;
          display: inline-block;
          font-size: 17px;
          margin: 0 auto;
          min-width: 0;
          padding: 12px 20px;
          text-align: center;
          border: 2px solid rgba(255,255,255,0.4);
          font-weight: 700;
          color: #111;
          box-shadow: 0 6px 16px rgba(0,0,0,0.12);
          contain: layout style paint;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .belt-info:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.16);
        }

        @media (prefers-reduced-motion: reduce) {
          .belt-track, .sell-right, .sell-left, .bg-image,
          .animate-float, .animate-drift, .animate-drift-rev {
            animation-duration: 0.01s !important;
            animation-iteration-count: 1 !important;
          }
        }

        @media (prefers-color-scheme: dark) {
          .main-title { color: #f1f1f1; }
          .first-desc, .desc {
            color: #f1f1f1;
            background: rgba(0,0,0,0.35);
          }
          .belt-info {
            background: rgba(0, 0, 0, 0.85);
            color: white;
            border: 2px solid rgba(255,255,255,0.25);
          }
        }

        @media (max-width: 768px) {
          .bg-image {
            transform: scale(0.7) translateZ(0) !important;
            opacity: 0.5 !important;
          }
          .sports-background-images {
            overflow: hidden;
          }
          .sell-row {
            height: 20px;
            font-size: 12px;
          }
          .desc {
            font-size: 20px;
            padding: 16px 20px;
          }
          .first-desc {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default SellSellSell;
